import {
  UserRouteActionV1Model,
  getHandledJobUids
} from '@core/models/entities/implementations/user-route-action-v1';
import { PlanEditGesture } from '@core/models/plan-edit-gesture.model';
import { PinnedStop, SessionGesture } from '@core/store/plan-edit/plan-edit.state';

/**
 * The action uids whose pin the gesture ends: released ones, and the moved actions DL unpins. A stop
 * moved to another route leaves its action empty, and the pin goes with it. A move into the route
 * its batch opens ends the pins it would end into any other.
 */
function releasedBy(gesture: PlanEditGesture): string[] {
  switch (gesture.kind) {
    case 'release-pin':
    case 'reorder':
    case 'move-stop':
      return [gesture.actionUid];
    case 'move-job':
      return gesture.releasesPin ? [gesture.fromActionUid] : [];
    case 'move-tour':
      return gesture.actionUids;
    case 'open-route':
      return gesture.then ? releasedBy(gesture.then) : [];
    default:
      return [];
  }
}

/** The stops DL holds pinned, by action uid: the gestures before `cursor` replayed over `pinsAtSave`. */
export function derivePins(
  pinsAtSave: Record<string, PinnedStop>,
  applied: SessionGesture[],
  cursor: number
): Record<string, PinnedStop> {
  const pins = { ...pinsAtSave };
  for (const { gesture } of applied.slice(0, cursor)) {
    if (gesture.kind === 'pin') {
      pins[gesture.actionUid] = { routeUid: gesture.routeUid, at: gesture.at };
    }
    for (const uid of releasedBy(gesture)) delete pins[uid];
    if (gesture.kind === 'remove-route') {
      for (const [uid, pin] of Object.entries(pins)) {
        if (pin.routeUid === gesture.routeUid) delete pins[uid];
      }
    }
  }
  return pins;
}

/**
 * The route starts DL holds, by route uid: the gestures before `cursor` replayed over
 * `startsAtSave`, as for pins.
 */
export function deriveRouteStarts(
  startsAtSave: Record<string, string>,
  applied: SessionGesture[],
  cursor: number
): Record<string, string> {
  const starts = { ...startsAtSave };
  for (const { gesture } of applied.slice(0, cursor)) {
    if (gesture.kind === 'remove-route') delete starts[gesture.routeUid];
    if (gesture.kind !== 'set-route-start') continue;
    if (gesture.at === null) delete starts[gesture.routeUid];
    else starts[gesture.routeUid] = gesture.at;
  }
  return starts;
}

/**
 * Whether moving one job out of `from` must also release its pin: `MoveJob` does not release the
 * pin of the stop it empties, so the gesture asks for it when the job was that stop's only one.
 */
export function jobMoveReleasesPin(
  from: UserRouteActionV1Model | undefined,
  pins: Record<string, PinnedStop>
): boolean {
  return !!from && getHandledJobUids(from).length === 1 && !!pins[from.uid];
}
